import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Eye, Save } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { useProfile } from "@/contexts/ProfileContext";
import { optionLabel } from "@/lib/profileLabels";
import { ChipGroup, FieldLabel } from "@/components/Chip";
import PhotoManager from "@/components/PhotoManager";

// Each section is one collapsible card.
function Section({ title, subtitle, testId, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="nvl-card" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <div>
          <h3 className="font-display text-2xl">{title}</h3>
          {subtitle ? <p className="mt-1 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{subtitle}</p> : null}
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open ? <div className="px-6 pb-6 pt-0 border-t" style={{ borderColor: "hsl(var(--nvl-border))" }}>{children}</div> : null}
    </div>
  );
}

function SaveBar({ onSave, saving, saved, error }) {
  const { lang } = useI18n();
  return (
    <div className="mt-4 flex items-center gap-3">
      <button onClick={onSave} disabled={saving} className="nvl-btn nvl-btn-primary disabled:opacity-40" data-testid="section-save">
        <Save size={14} className="mr-2" /> {saving ? (lang==="it"?"Salvataggio…":"Saving…") : (lang==="it"?"Salva":"Save")}
      </button>
      {saved ? <span className="text-xs" style={{color:"hsl(var(--nvl-accent))"}}>{lang==="it"?"Salvato":"Saved"}</span> : null}
      {error ? <span className="text-xs" style={{color:"hsl(var(--destructive))"}}>{error}</span> : null}
    </div>
  );
}

function useSectionSaver(sectionName) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const save = async (payload) => {
    setError(""); setSaved(false); setSaving(true);
    try {
      const { data } = await api.patch(`/profile/section/${sectionName}`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      return data;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setSaving(false);
    }
  };
  return { save, saving, saved, error };
}

export default function EditProfile() {
  const { lang } = useI18n();
  const { refresh } = useProfile();
  const [profile, setProfile] = useState(null);
  const [options, setOptions] = useState({});
  const [completion, setCompletion] = useState({ score: 0 });

  useEffect(() => {
    (async () => {
      const [pRes, oRes] = await Promise.all([api.get("/profile/me"), api.get("/profile/options")]);
      setProfile(pRes.data.profile || {});
      setOptions(oRes.data || {});
      setCompletion(pRes.data.completion || { score: 0 });
    })();
  }, []);

  const reload = async () => {
    const { data } = await api.get("/profile/me");
    setProfile(data.profile);
    setCompletion(data.completion || { score: 0 });
    refresh?.();
  };

  const label = (kind, key) => optionLabel(lang, kind, key);

  if (!profile) {
    return <div className="text-sm" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Caricamento…":"Loading…"}</div>;
  }

  // Section states
  return (
    <div className="max-w-4xl" data-testid="edit-profile-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {lang === "it" ? "Modifica profilo" : "Edit profile"}
      </p>
      <h1 className="mt-3 text-4xl md:text-5xl">{lang==="it"?"Il mio profilo":"My profile"}</h1>

      <div className="mt-6 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <p className="text-xs uppercase tracking-widest" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Completezza":"Completion"} · <span data-testid="completion-score">{completion.score}%</span></p>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{backgroundColor:"hsl(var(--nvl-border))"}}>
            <div className="h-full" style={{ width: `${completion.score}%`, backgroundColor: "hsl(var(--nvl-accent))", transition: "width 300ms" }} />
          </div>
        </div>
        <Link to="/app/profile/preview" className="nvl-btn nvl-btn-outline" data-testid="link-preview">
          <Eye size={14} className="mr-2" /> {lang==="it"?"Anteprima profilo":"Preview profile"}
        </Link>
      </div>

      <div className="mt-8 space-y-4">
        <PhotosSection profile={profile} onSaved={reload} />
        <BasicSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <AboutMeSectionEdit profile={profile} onSaved={reload} />
        <LookingForSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <InterestsSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <ValuesSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <LifestyleSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <FamilySectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <ReligionSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <CommunicationSectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <CommunitySectionEdit profile={profile} options={options} onSaved={reload} label={label} />
        <PrivacySectionEdit profile={profile} onSaved={reload} />
        <VerificationSection profile={profile} onSaved={reload} />
      </div>
    </div>
  );
}

function PhotosSection({ profile, onSaved }) {
  const { lang } = useI18n();
  return (
    <Section title={lang==="it"?"Foto":"Photos"} testId="section-photos" defaultOpen>
      <PhotoManager photos={profile.photos || []} onChange={onSaved} />
    </Section>
  );
}

function BasicSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({
    first_name: profile.first_name || "",
    display_name: profile.display_name || "",
    birthdate: profile.birthdate || "",
    gender: profile.gender || null,
    sexual_orientation: profile.sexual_orientation || null,
    country: profile.country || "",
    city: profile.city || "",
    height_cm: profile.height_cm || "",
    languages: profile.languages || [],
  });
  const { save, saving, saved, error } = useSectionSaver("basic");
  return (
    <Section title={lang==="it"?"Informazioni di base":"Basic information"} testId="section-basic">
      <div className="space-y-4 mt-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><FieldLabel>{lang==="it"?"Nome":"First name"}</FieldLabel><input className="nvl-input mt-2" value={v.first_name} onChange={(e)=>setV({...v, first_name:e.target.value})} /></div>
          <div><FieldLabel required>{lang==="it"?"Nome visualizzato":"Display name"}</FieldLabel><input className="nvl-input mt-2" value={v.display_name} onChange={(e)=>setV({...v, display_name:e.target.value})} /></div>
        </div>
        <div>
          <FieldLabel required>{lang==="it"?"Data di nascita":"Birthdate"}</FieldLabel>
          <input type="date" className="nvl-input mt-2" value={v.birthdate} onChange={(e)=>setV({...v, birthdate:e.target.value})} max={new Date(new Date().setFullYear(new Date().getFullYear()-18)).toISOString().split("T")[0]} />
        </div>
        <div><FieldLabel>{lang==="it"?"Genere":"Gender"}</FieldLabel><div className="mt-2"><ChipGroup options={options.gender||[]} value={v.gender} onChange={(x)=>setV({...v, gender:x})} labelFor={(o)=>label("gender",o)} /></div></div>
        <div><FieldLabel>{lang==="it"?"Orientamento":"Orientation"}</FieldLabel><div className="mt-2"><ChipGroup options={options.sexual_orientation||[]} value={v.sexual_orientation} onChange={(x)=>setV({...v, sexual_orientation:x})} labelFor={(o)=>label("sexual_orientation",o)} /></div></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><FieldLabel>{lang==="it"?"Paese":"Country"}</FieldLabel><input className="nvl-input mt-2" value={v.country} onChange={(e)=>setV({...v, country:e.target.value})} /></div>
          <div><FieldLabel>{lang==="it"?"Città":"City"}</FieldLabel><input className="nvl-input mt-2" value={v.city} onChange={(e)=>setV({...v, city:e.target.value})} /></div>
        </div>
        <div><FieldLabel>{lang==="it"?"Altezza (cm)":"Height (cm)"}</FieldLabel><input type="number" min={120} max={230} className="nvl-input mt-2" value={v.height_cm} onChange={(e)=>setV({...v, height_cm:Number(e.target.value)||null})} /></div>
        <div><FieldLabel>{lang==="it"?"Lingue":"Languages"}</FieldLabel><div className="mt-2"><ChipGroup multi max={8} options={options.languages||[]} value={v.languages} onChange={(x)=>setV({...v, languages:x})} labelFor={(o)=>label("languages",o)} /></div></div>
        <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
      </div>
    </Section>
  );
}

function AboutMeSectionEdit({ profile, onSaved }) {
  const { lang } = useI18n();
  const [bio, setBio] = useState(profile.bio || "");
  const { save, saving, saved, error } = useSectionSaver("about_me");
  return (
    <Section title={lang==="it"?"Su di me":"About me"} testId="section-about_me">
      <textarea className="nvl-input mt-4 min-h-[160px]" maxLength={1000} value={bio} onChange={(e)=>setBio(e.target.value)} />
      <div className="mt-2 text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>{bio.length} / 1000</div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save({bio}); onSaved?.(); }} />
    </Section>
  );
}

function LookingForSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({
    relationship_goals: profile.relationship_goals || [],
    interested_in_genders: profile.interested_in_genders || [],
    age_min: profile.age_min ?? 25, age_max: profile.age_max ?? 55, distance_km: profile.distance_km ?? 100,
  });
  const { save, saving, saved, error } = useSectionSaver("looking_for");
  return (
    <Section title={lang==="it"?"Preferenze di relazione":"Relationship preferences"} testId="section-looking_for">
      <div className="space-y-5 mt-4">
        <div><FieldLabel>{lang==="it"?"Cosa cerchi":"What you're looking for"}</FieldLabel><div className="mt-2"><ChipGroup multi options={options.relationship_goals||[]} value={v.relationship_goals} onChange={(x)=>setV({...v, relationship_goals:x})} labelFor={(o)=>label("relationship_goals",o)} /></div></div>
        <div><FieldLabel>{lang==="it"?"Chi ti interessa":"Interested in"}</FieldLabel><div className="mt-2"><ChipGroup multi options={options.interested_in||[]} value={v.interested_in_genders} onChange={(x)=>setV({...v, interested_in_genders:x})} labelFor={(o)=>label("interested_in",o)} /></div></div>
        <div className="grid grid-cols-3 gap-4">
          <div><FieldLabel>{lang==="it"?"Età min":"Age min"}</FieldLabel><input type="number" className="nvl-input mt-2" value={v.age_min} onChange={(e)=>setV({...v, age_min:Number(e.target.value)})} /></div>
          <div><FieldLabel>{lang==="it"?"Età max":"Age max"}</FieldLabel><input type="number" className="nvl-input mt-2" value={v.age_max} onChange={(e)=>setV({...v, age_max:Number(e.target.value)})} /></div>
          <div><FieldLabel>{lang==="it"?"Distanza (km)":"Distance (km)"}</FieldLabel><input type="number" className="nvl-input mt-2" value={v.distance_km} onChange={(e)=>setV({...v, distance_km:Number(e.target.value)})} /></div>
        </div>
        <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
      </div>
    </Section>
  );
}

function InterestsSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState(profile.interests || []);
  const { save, saving, saved, error } = useSectionSaver("interests");
  return (
    <Section title={lang==="it"?"Interessi":"Interests"} testId="section-interests">
      <div className="mt-4"><ChipGroup multi options={options.interests||[]} value={v} onChange={setV} labelFor={(o)=>label("interests",o)} /></div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save({interests:v}); onSaved?.(); }} />
    </Section>
  );
}

function ValuesSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({ values: profile.values||[], top_values: profile.top_values||[] });
  const { save, saving, saved, error } = useSectionSaver("values");
  return (
    <Section title={lang==="it"?"I miei valori":"My values"} testId="section-values">
      <div className="mt-4 space-y-5">
        <div><FieldLabel>{lang==="it"?"Valori":"Values"}</FieldLabel><div className="mt-2"><ChipGroup multi options={options.values||[]} value={v.values} onChange={(x)=>setV({...v, values:x, top_values:v.top_values.filter(y=>x.includes(y))})} labelFor={(o)=>label("values",o)} /></div></div>
        <div><FieldLabel>{lang==="it"?"5 più importanti":"Top 5"}</FieldLabel><div className="mt-2"><ChipGroup multi max={5} options={v.values} value={v.top_values} onChange={(x)=>setV({...v, top_values:x})} labelFor={(o)=>label("values",o)} /></div></div>
        <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
      </div>
    </Section>
  );
}

function LifestyleSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({ smoking: profile.smoking||null, alcohol: profile.alcohol||null, pets: profile.pets||null, lifestyle_type: profile.lifestyle_type||null, diet: profile.diet||null });
  const { save, saving, saved, error } = useSectionSaver("lifestyle");
  return (
    <Section title={lang==="it"?"Stile di vita":"Lifestyle"} testId="section-lifestyle">
      <div className="mt-4 grid md:grid-cols-2 gap-5">
        {["lifestyle_type","smoking","alcohol","pets","diet"].map((k)=>(
          <div key={k}><FieldLabel>{k}</FieldLabel><div className="mt-2"><ChipGroup options={options[k]||[]} value={v[k]} onChange={(x)=>setV({...v, [k]:x})} labelFor={(o)=>label(k,o)} /></div></div>
        ))}
      </div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
    </Section>
  );
}

function FamilySectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({ relationship_status: profile.relationship_status||null, children: profile.children||null, wants_children: profile.wants_children||null, open_to_children: profile.open_to_children||null });
  const { save, saving, saved, error } = useSectionSaver("family");
  return (
    <Section title={lang==="it"?"Famiglia":"Family"} testId="section-family">
      <div className="mt-4 grid md:grid-cols-2 gap-5">
        {["relationship_status","children","wants_children","open_to_children"].map((k)=>(
          <div key={k}><FieldLabel>{k}</FieldLabel><div className="mt-2"><ChipGroup options={options[k]||[]} value={v[k]} onChange={(x)=>setV({...v, [k]:x})} labelFor={(o)=>label(k,o)} /></div></div>
        ))}
      </div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
    </Section>
  );
}

function ReligionSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({ religion: profile.religion||null, religion_importance: profile.religion_importance||null });
  const { save, saving, saved, error } = useSectionSaver("religion");
  return (
    <Section title={lang==="it"?"Religione & spiritualità":"Religion & spirituality"} testId="section-religion">
      <div className="mt-4 grid md:grid-cols-2 gap-5">
        <div><FieldLabel>{lang==="it"?"Religione":"Religion"}</FieldLabel><div className="mt-2"><ChipGroup options={options.religion||[]} value={v.religion} onChange={(x)=>setV({...v, religion:x})} labelFor={(o)=>label("religion",o)} /></div></div>
        <div><FieldLabel>{lang==="it"?"Importanza":"Importance"}</FieldLabel><div className="mt-2"><ChipGroup options={options.religion_importance||[]} value={v.religion_importance} onChange={(x)=>setV({...v, religion_importance:x})} labelFor={(o)=>label("religion_importance",o)} /></div></div>
      </div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
    </Section>
  );
}

function CommunicationSectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState(profile.communication_style || []);
  const { save, saving, saved, error } = useSectionSaver("communication");
  return (
    <Section title={lang==="it"?"Stile di comunicazione":"Communication style"} testId="section-communication">
      <div className="mt-4"><ChipGroup multi options={options.communication_style||[]} value={v} onChange={setV} labelFor={(o)=>label("communication_style",o)} /></div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save({communication_style:v}); onSaved?.(); }} />
    </Section>
  );
}

function CommunitySectionEdit({ profile, options, onSaved, label }) {
  const { lang } = useI18n();
  const [v, setV] = useState({ covid_perspective: profile.covid_perspective||null, covid_motivations: profile.covid_motivations||[], covid_public: !!profile.covid_public });
  const { save, saving, saved, error } = useSectionSaver("community");
  return (
    <Section title={lang==="it"?"COVID-19 & Community":"COVID-19 & Community"} testId="section-community">
      <div className="mt-4 space-y-5">
        <p className="text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Facoltativo. Nessuna prova richiesta.":"Optional. No proof required."}</p>
        <div><FieldLabel>{lang==="it"?"Prospettiva":"Perspective"}</FieldLabel><div className="mt-2"><ChipGroup options={options.covid_perspective||[]} value={v.covid_perspective} onChange={(x)=>setV({...v, covid_perspective:x})} labelFor={(o)=>label("covid_perspective",o)} /></div></div>
        <div><FieldLabel>{lang==="it"?"Motivazioni":"Motivations"}</FieldLabel><div className="mt-2"><ChipGroup multi options={options.covid_motivations||[]} value={v.covid_motivations} onChange={(x)=>setV({...v, covid_motivations:x})} labelFor={(o)=>label("covid_motivations",o)} /></div></div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={v.covid_public} onChange={(e)=>setV({...v, covid_public:e.target.checked})} data-testid="community-public-toggle" />
          {lang==="it"?"Rendi visibile sul profilo pubblico.":"Show on my public profile."}
        </label>
        <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
      </div>
    </Section>
  );
}

function PrivacySectionEdit({ profile, onSaved }) {
  const { lang } = useI18n();
  const priv = profile.privacy || {};
  const [v, setV] = useState(priv);
  const { save, saving, saved, error } = useSectionSaver("privacy");
  const KEYS = [
    ["show_age", lang==="it"?"Mostra età":"Show age"],
    ["show_city", lang==="it"?"Mostra città":"Show city"],
    ["show_distance", lang==="it"?"Mostra distanza approssimativa":"Show approximate distance"],
    ["show_photos", lang==="it"?"Mostra foto":"Show photos"],
    ["show_video", lang==="it"?"Mostra video":"Show video"],
    ["show_bio", lang==="it"?"Mostra bio":"Show bio"],
    ["show_interests", lang==="it"?"Mostra interessi":"Show interests"],
    ["show_values", lang==="it"?"Mostra valori":"Show values"],
    ["show_lifestyle", lang==="it"?"Mostra stile di vita":"Show lifestyle"],
    ["show_family", lang==="it"?"Mostra famiglia":"Show family"],
    ["show_education", lang==="it"?"Mostra istruzione":"Show education"],
    ["show_communication_style", lang==="it"?"Mostra stile di comunicazione":"Show communication style"],
    ["show_religion", lang==="it"?"Mostra religione":"Show religion"],
    ["show_covid_perspective", lang==="it"?"Mostra prospettiva COVID":"Show COVID perspective"],
  ];
  return (
    <Section title={lang==="it"?"Privacy e visibilità":"Privacy & visibility"} testId="section-privacy">
      <p className="mt-3 text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>
        {lang==="it"?"La tua posizione esatta non viene mai mostrata.":"Your exact location is never shown."}
      </p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {KEYS.map(([k,lbl]) => (
          <label key={k} className="flex items-center justify-between gap-3 nvl-card px-4 py-3 text-sm">
            <span>{lbl}</span>
            <input type="checkbox" checked={!!v[k]} onChange={(e)=>setV({...v, [k]:e.target.checked})} data-testid={`privacy-${k}`} />
          </label>
        ))}
      </div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={async ()=>{ await save(v); onSaved?.(); }} />
    </Section>
  );
}

function VerificationSection({ profile, onSaved }) {
  const { lang } = useI18n();
  const status = profile.verification?.status || "not_verified";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const T = {
    not_verified: lang==="it"?"Non verificato":"Not verified",
    pending: lang==="it"?"In attesa di verifica":"Verification pending",
    verified: lang==="it"?"Identità verificata":"Identity verified",
    failed: lang==="it"?"Verifica fallita":"Verification failed",
    expired: lang==="it"?"Verifica scaduta":"Verification expired",
  };

  const request = async () => {
    setError(""); setSaving(true);
    try {
      await api.post("/profile/verification/request");
      onSaved?.();
    } catch (e) {
      setError(extractError(e));
    } finally { setSaving(false); }
  };

  return (
    <Section title={lang==="it"?"Verifica identità":"Identity verification"} testId="section-verification">
      <div className="mt-4">
        <p className="text-sm" style={{color:"hsl(var(--nvl-ink-soft))"}}>
          {lang==="it"
            ? "I profili verificati contribuiscono a una community più sicura e affidabile."
            : "Verified identity profiles help create a safer and more trustworthy community."}
        </p>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
            style={{
              backgroundColor: status === "verified" ? "hsl(var(--nvl-accent))" : "hsl(var(--muted))",
              color: status === "verified" ? "hsl(var(--nvl-bg))" : "hsl(var(--nvl-ink))",
            }}
            data-testid="verification-status"
          >
            {status === "verified" ? "✓ " : ""}{T[status] || status}
          </span>
          {status === "not_verified" || status === "failed" || status === "expired" ? (
            <button onClick={request} disabled={saving} className="nvl-btn nvl-btn-outline" data-testid="verification-request">
              {lang==="it"?"Richiedi verifica":"Request verification"}
            </button>
          ) : null}
        </div>
        <p className="mt-4 text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>
          {lang==="it"
            ? "La verifica sarà completata da un partner esterno in una fase successiva. Nessun documento viene richiesto ora."
            : "Verification will be completed by an external partner in a future phase. No documents are collected now."}
        </p>
        {error ? <div className="mt-2 text-xs" style={{color:"hsl(var(--destructive))"}}>{error}</div> : null}
      </div>
    </Section>
  );
}
